import React from 'react'
import { Box, Button, Paper, Snackbar, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import { useGlobalState } from 'state'
import { Map } from 'components/Map'
import { isPrivateMode, isPublicMode, manualURL } from 'common/consts'

import { MenuDrawer } from 'components/containers/MenuDrawer'
import { ObservatoryContainer } from 'components/containers/ObservatoryContainer'
import { StageChartContainer } from 'components/containers/StageChartContainer'
import { MonitorContainer } from 'components/containers/MonitorContainer'
import { RightContainer } from 'components/containers/RightContainer'

import { LoadingDialog } from 'components/dialogs/LoadingDialog'

import { StyleControl } from 'components/controls/StyleControl'
import { BaseDateControl } from 'components/controls/BaseDateControl'
import { LegendControl } from 'components/controls/LegendControl'
import { ZoomControl } from 'components/controls/ZoomControl'
import { OpacityControl } from 'components/controls/OpacityControl'
import { HazardMapControl } from 'components/controls/HazardMapControl'

import { BorderLayer } from 'components/layers/BorderLayer'
import { TimeSeriesSliderLayers } from 'components/layers/TimeSeriesSliderLayers'
import { ObservatoryLayer } from 'components/layers/ObservatoryLayer'
import { RoadRegulationLayer } from 'components/layers/RoadRegulationLayer'
import { HazardMapLayer } from 'components/layers/HazardMapLayer'
import { GsiVectorLayer } from 'components/layers/GsiVectorLayer'
import { BuildingPlateauLayer } from 'components/layers/BuildingPlateauLayer'
import { GeographicLayer } from 'components/layers/GeographicLayer'
import { RiskTextControl } from 'components/controls/RiskTextControl'
import { HazardMapLegendControl } from 'components/controls/HazardMapLegendControl'
import { HinanAreaControl } from 'components/controls/HinanAreaControl'
import { TrialOperationControl } from 'components/controls/TrialOperationControl'
import { DescriptionControl } from 'components/layers/DescriptionControl'
import { EstimatedFloodingLegendControl } from 'components/controls/EstimatedFloodingLegendControl'
import { HinanAreaLayer } from 'components/layers/HinanAreaLayer'
import { HinanAreaNameLayer } from 'components/layers/HinanAreaNameLayer'
import { HinanListControl } from 'components/controls/HinanListControl'
import { blue, blueGrey, grey } from '@mui/material/colors'
import { NoticeDialog } from 'components/dialogs/NoticeDialog'
import CircleIcon from '@mui/icons-material/Circle'
import { RoadRegulationControl } from 'components/controls/RoadRegulationControl'
import { appName } from 'common/config'
import iconPng from 'assets/icon.png'
import { LegendControlButton } from 'components/ui/LegendControlButton'
import { UnderpassListContainer } from 'components/containers/UnderpassListContainer'

const TopToggleButton = ({value, label, checked}: {value: string, label: string, checked: boolean}) => {
  return (
    <ToggleButton value={value} sx={{minWidth: 90, fontSize: '12px'}}>
      <Stack direction={'row'} alignItems={'center'}>
        <CircleIcon fontSize='inherit' sx={{color: checked ? blue[700] : grey[300], mr: '4px'}} />
        <Typography variant='caption' fontWeight={'bold'}>{label}</Typography>
      </Stack>
    </ToggleButton>
  )
}

export const DeskTop = () => {
  const state = useGlobalState()
  const [controls, setControls] = React.useState<string[]>([])
  const [visibleLegend, setVisibleLegend] = React.useState<boolean>(false)

  const isRiverFloods = React.useMemo(() => {
    return state.app.menu === 'river-floods'
  }, [state.app.menu])

  React.useEffect(() => {
    if (state.mapbox) {
      state.mapbox.resize()
    }
  }, [
    state.app.observatoryContainer,
    state.app.monitorContainer,
    state.app.rightContainer
  ])

  return (
    <Stack sx={{ height: '100dvh' }}>
      { isPublicMode &&
        <Stack height={'34px'} direction={'row'} alignItems={'center'} pl={1} sx={{backgroundColor: blueGrey[800]}}>
          <img src={iconPng} alt={appName} width={20} height={20} />
          <Typography sx={{ flexGrow: 1, color: 'white', fontSize: '16px', pl: 1, fontWeight: 'bold' }}>{appName}</Typography>
          <Button
            variant='text'
            size='small'
            sx={{color: 'white', mr: 1}}
            onClick={() => window.open(manualURL, '_blank')}
          >操作マニュアル</Button>
        </Stack>
      }
      <Stack direction={'row'} flexGrow={1} sx={{ overflow: 'hidden' }}>
        { isPrivateMode && <MenuDrawer /> }
        <Box position={'relative'} width={'100%'}>
          <Map>
            <Stack sx={{position: 'absolute', zIndex: 1, bottom: 80, left: 10, pointerEvents: 'none'}} spacing={1} alignItems={'flex-end'}>
              <ZoomControl />
            </Stack>
            { (isRiverFloods || isPrivateMode) && <HazardMapLayer /> }

            <Stack direction={'column'} alignItems={'flex-start'} spacing={1} sx={{position: 'absolute', zIndex: 1, top: 10, left: 10}}>
              <Paper>
                <ToggleButtonGroup
                  value={controls}
                  onChange={(e, newControls) => {setControls(newControls)}}
                  size='small'
                >
                  <TopToggleButton value='observatory' label='観測所' checked={controls.includes('observatory')} />
                  <TopToggleButton value='setting' label='レイヤ' checked={controls.includes('setting')} />
                  { isRiverFloods &&  <TopToggleButton value='school' label='避難対象地区一覧' checked={controls.includes('school')} /> }
                </ToggleButtonGroup>
              </Paper>
            </Stack>

            <BorderLayer />
            <GsiVectorLayer />
            <BuildingPlateauLayer />
            <RoadRegulationLayer />

            <Stack direction={'row'} sx={{position: 'absolute', zIndex: 1, top: 60, left: 10}}>
              <Stack direction={'column'} alignItems={'flex-start'} spacing={1} mr={1}>
                <ObservatoryLayer showControl={controls.includes('observatory')} />
                { controls.includes('setting') && (
                  <>
                    <Stack component={Paper} px={1} pb={1} width={160} spacing={'1px'}>
                      <HinanAreaControl />
                      { isRiverFloods && <EstimatedFloodingLegendControl /> }
                      { isPrivateMode && <RoadRegulationControl /> }
                    </Stack>
                    <OpacityControl />
                  </>
                )}
                { isPrivateMode && <StyleControl /> }
                { isPrivateMode && <HazardMapControl /> }
                { isPrivateMode && <GeographicLayer /> }
              </Stack>
              { isRiverFloods &&
                <Stack sx={{backgroundColor: grey[50]}} maxHeight={'calc(100dvh - 260px)'} overflow={'auto'}>
                  { controls.includes('school') && <HinanListControl /> }
                </Stack>
              }
            </Stack>
            { isRiverFloods && (
              <>
                <Stack sx={{position: 'absolute', zIndex: 1, top: 10, right: 10, pointerEvents: 'none'}} spacing={1}>
                  { isPublicMode && <TrialOperationControl /> }
                </Stack>
                <Stack sx={{position: 'absolute', zIndex: 1, bottom: 140, right: 10, pointerEvents: 'none'}} spacing={1}>
                  { visibleLegend && <DescriptionControl /> }
                </Stack>
                <HinanAreaLayer />
                <HinanAreaNameLayer />
              </>
            )}
            <Stack sx={{position: 'absolute', zIndex: 1, top: isPrivateMode ? 10 : 180, right: 10, pointerEvents: 'none'}} spacing={1} alignItems={'flex-end'}>
              { isPrivateMode && <RiskTextControl /> }
              <LegendControlButton onClick={setVisibleLegend} isVisible={visibleLegend} />
              { visibleLegend && (
                <>
                  <LegendControl />
                  <HazardMapLegendControl />
                </>
              )}
            </Stack>

            <Stack direction="row" alignItems={'flex-end'} sx={{position: 'absolute', zIndex: 1, bottom: 48, left: 40, right: 10}}>
              <Stack flexGrow={1} px={3}>
                <TimeSeriesSliderLayers />
              </Stack>
              <Stack width={150}>
                <BaseDateControl/>
              </Stack>
            </Stack>
          </Map>
        </Box>
        { state.app.observatoryContainer && (
          <Box sx={{ minWidth: 400, borderLeft: '1px solid #9e9e9e' }}>
            <ObservatoryContainer />
          </Box>
        ) }
        { state.app.monitorContainer === 'stage' && <StageChartContainer /> }
        { state.app.monitorContainer === 'underpass' && <UnderpassListContainer /> }
        { state.app.monitorContainer === 'monitor' && <MonitorContainer width={400} /> }
        { (!state.app.observatoryContainer && state.app.rightContainer) && <RightContainer width={300} /> }
        <LoadingDialog />
        <Snackbar
          open={state.app.snackbarMessage !== ''}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => state.app.setSnackbarMessage('')}
          message={state.app.snackbarMessage}
        />
        { isPublicMode && <NoticeDialog /> }
      </Stack>
    </Stack>
  )
}
